import Badge from "@common/controls/Badge/component";
import Button from "@common/controls/Button/component";
import { isGroupItem } from "@common/controls/Grid/lib";
import InfiniteScroll from "@common/controls/InfiniteScroll/component";
import Select from "@common/controls/Inputs/Select/component";
import DeleteModal from "@common/controls/Modal/Modals/Delete/component";
import { type GenericMap, IconType, SystemColor } from "@common/declarations";
import withDropdownHOC from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import { useI18n } from "@common/hooks/i18n";
import { getPage } from "@common/lib/array";
import { type I18n } from "@common/lib/i18n";
import { parseQueryString } from "@common/lib/url";
import classNames from "classnames";
import { cloneDeep, isEqual, isFunction, isNull, isUndefined } from "lodash";
import * as React from "react";
import DownloadLink from "../DownloadLink/component";
import { type GridGroupInfo } from "../Grid/types";
import Icon from "../Icon/component";
import ExpandableSearch from "../Inputs/Search/Expandable";
import ActionBarButtons from "./ActionBarButtons";
import { PreviousNextPager } from "./Pager/component";
import { groupData } from "./lib";
import {
  type Filter,
  type FilterContentProps,
  type FilterValue,
  type GridContentProps,
  type GridViewAddPropsBase,
  type GridViewBaseConfigProps,
  type GridViewEditPropsBase,
  type GridViewGroupingInfo,
  type GridViewRemoveModalInfo,
  type GridViewRemoveModalProps,
  type GridViewRemovePropsBase,
  type GridViewToolbarButton,
  type GridViewToolbarButtonDef
} from "./types";

type VisibleModal = "Add" | "Edit" | "Delete" | null;

interface State<T, D> {
  deletingItem?: T | null;
  editingItem: Partial<D> | null;
  originalItem: Partial<D> | null;
  data: T[];
  isAdding: boolean;
  isModalActionInProgress: boolean;
  isModalLoading: boolean;
  isModalReadOnly: boolean;
  visibleModal: VisibleModal;

  modalData: object;
  pageNumber: number;
  pageSize: number;
  hasMore: boolean;
  hasPageSizeChanged: boolean;
  searchText: string;
  filterValues: GenericMap<FilterValue>;
  selectedItems?: T[];
}

type GridViewBaseProps<T, D, ModalProps = void> = GridViewBaseConfigProps<T, D, ModalProps> & {
  children: (props: GridContentProps<T>) => any;
  data: T[];
  grouping?: GridViewGroupingInfo<T>;
  filters?: Filter<T>[];
  i18n: I18n;
  isLoading?: boolean;
  searchText?: string | null;
};

type FilterDropdownComponentProps<T> = {
  Content: React.ComponentType<FilterContentProps<T>>;
};
const FilterDropdownComponent = withDropdownHOC<FilterDropdownComponentProps<any> & FilterContentProps<any>>(
  (tp) => {
    const i18n = useI18n();

    return (
      <span
        ref={tp.toggleRef}
        onClick={tp.toggleOpen}
        className={classNames("select", { open: tp.isOpen })}
        data-name="filter"
        style={tp.style}
      >
        {i18n.filter}
        <Icon className="toggle" icon={tp.isOpen ? IconType.ChevronUp : IconType.ChevronDown} />
      </span>
    );
  },
  (cp) => (
    <cp.Content
      filters={cp.filters}
      onChange={(name, value) => (cp.close(), cp.onChange(name, value))}
      values={cp.values}
    />
  )
);

export default class GridViewBase<T extends {}, D, ModalProps = void> extends React.Component<
  GridViewBaseProps<T, D, ModalProps>,
  State<T, D>
> {
  // if a component has been unmounted, calling setState will cause an error.
  // we pass some methods as callbacks to external modals, and we have no controls when these callbacks will be called
  private _contentRef = React.createRef<HTMLDivElement>();

  public static defaultProps = {
    toolbar: {
      showAdd: true,
      showSearch: true
    }
  };

  constructor(props: GridViewBaseProps<T, D, ModalProps>) {
    super(props);

    const pageNumber = props.pageNumber ?? 1;
    // TODO: proper handling for trees
    const pageSize = props.parentIdProp ? 100000 : props.pageSize || 25;

    const searchText = props.searchText ?? (props.useSearchUrl === false ? undefined : parseQueryString().search);
    this.state = {
      pageSize,
      data: searchText ? props.data : this._getPagedData(props.data, pageNumber, pageSize),
      deletingItem: null,
      editingItem: null,
      originalItem: null,
      isAdding: false,
      isModalActionInProgress: false,
      isModalLoading: false,
      visibleModal: null,
      modalData: {},
      pageNumber,
      hasPageSizeChanged: false,
      hasMore: false,
      isModalReadOnly: false,
      searchText,
      filterValues: (this.props.filters ?? []).reduce((res, x) => ({ ...res, [x.name]: x.value }), {}),
      selectedItems: props.selectedItems
    };
  }

  public componentDidMount() {
    if (!isFunction(this.props.remove) && this.props.remove?.item) {
      this.setState({
        deletingItem: this.props.remove?.item,
        visibleModal: "Delete"
      });
    }

    if (this.props.add?.defaultIsVisible) {
      this.onAddClick(); // NOTE: may have unpredictable consequences
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: GridViewBaseProps<T, D, ModalProps>) {
    if (
      this.props.pageNumber !== nextProps.pageNumber ||
      this.props.pageSize !== nextProps.pageSize ||
      this.props.data !== nextProps.data
    ) {
      const pageNumber =
        (this.props.pageNumber === nextProps.pageNumber ? this.state.pageNumber : nextProps.pageNumber) ??
        this.state.pageNumber;
      const pageSize =
        (this.props.pageSize === nextProps.pageSize ? this.state.pageSize : nextProps.pageSize) ?? this.state.pageSize;

      let data = nextProps.onPageChanged ? nextProps.data : this._getPagedData(nextProps.data, pageNumber, pageSize);

      // for managed grids we accumulate data in manager
      if (this.props.pagerType === "scroll" && !nextProps.onPageChanged) {
        // preserve data if there's no change in the data itself (e.g. page number/size has changed, but data is old)
        data = this.props.data === nextProps.data ? this.state.data : [...this.state.data, ...data];
      }

      if (this.props.pagerType === "scroll" && this.props.data !== nextProps.data) {
        this.setState({
          hasMore: nextProps.data.length >= pageNumber * pageSize
        });
      }

      this.setState({
        pageNumber,
        pageSize,
        data
      });
    }
  }

  public componentDidUpdate(prevProps: GridViewBaseProps<T, D, ModalProps>, prevState: State<T, D>) {
    if (!isEqual(this.props.filters, prevProps.filters))
      this.setState({
        filterValues: (this.props.filters ?? []).reduce((res, x) => ({ ...res, [x.name]: x.value }), {})
      });

    if (
      !isFunction(this.props.remove) &&
      !isFunction(prevProps.remove) &&
      !isEqual(this.props.remove?.item, prevProps.remove?.item)
    ) {
      this.setState({
        deletingItem: this.props.remove?.item,
        visibleModal: this.props.remove?.item ? "Delete" : null
      });
    }
  }

  private _getPagedData(data: T[], pageNumber: number, pageSize: number) {
    if (!!this.props.onPageChanged) {
      return data;
    }

    return getPage(data, pageNumber, pageSize);
  }

  private _onPageChange = (pageNumber: number) => {
    if (this.props.onPageChanged) {
      this.props.onPageChanged(pageNumber, this.state.pageSize);
      this.setState({
        pageNumber
      });
      return;
    }

    const data = this._getPagedData(this.props.data, pageNumber, this.state.pageSize);
    this.setState({
      pageNumber,
      data: this.props.pagerType === "scroll" ? [...this.state.data, ...data] : data,
      hasMore: this.props.pagerType === "scroll" && data.length >= this.state.pageSize
    });
  };

  private _onPageSizeChange = (pageSize: number) => {
    if (this.props.onPageChanged) {
      this.props.onPageChanged(1, pageSize);
      this.setState({
        pageNumber: 1,
        pageSize,
        hasPageSizeChanged: true
      });
      return;
    }

    this.setState({
      pageSize,
      hasPageSizeChanged: true,
      pageNumber: 1,
      data: this._getPagedData(this.props.data, 1, pageSize)
    });
  };

  private _onPreviousPage = () => this._onPageChange(this.state.pageNumber - 1);
  private _onNextPage = () => this._onPageChange(this.state.pageNumber + 1);

  private get _showPager() {
    if (this.props.pagerType === "scroll") return false;

    return (
      (!!this.props.onPageChanged && this.props.data.length >= (this.props.pageSize ?? Number.MAX_VALUE)) || // BE pagination and data length >= page size (> in case of grouping)
      (this.props.data.length > this.state.pageSize &&
        this.state.data.length <= this.state.pageSize &&
        !this.state.searchText) || // FE pagination
      this.state.pageNumber > 1 ||
      (this.state.hasPageSizeChanged && !this.state.searchText)
    );
  }

  private get _isFiltered() {
    return (
      !!this.state.searchText ||
      this.state.pageNumber > 1 ||
      Object.keys(this.state.filterValues).some(
        (x) =>
          !isNull(this.state.filterValues[x]) &&
          this.state.filterValues[x] !== this.props.filters?.find((f) => f.name === x)?.defaultValue
      )
    );
  }

  private get _noDataMode() {
    return this._showPager ? "grid" : this._isFiltered ? "title" : this.props.noDataMode;
  }

  public render() {
    const { className, gridClassName, header, getRowClassName, pageSizes, i18n } = this.props;
    const { data, pageNumber, pageSize } = this.state;

    const noDataText = this._isFiltered ? i18n.nothingHere : this.props.noDataText;
    const showPager = this._showPager;
    const hasMore = this.state.data.length >= this.state.pageSize;

    const content = (
      <div ref={this._contentRef} className="content">
        {this.props.children({
          className: gridClassName,
          data: groupData(data, this.props.idKey, this.props.grouping, this.props.sortedBy) as any,
          filterFn: this._onSearch,
          getRowClassName,
          pagerType: this.props.pagerType,
          hint: this.props.hint,
          getIsItemClickable: this._getIsItemClickable,
          idKey: this.props.idKey,
          isLastPage: !showPager || !hasMore,
          isLoading: this.props.isLoading ?? false,
          noDataMode: this._noDataMode,
          noTourStep: this.props.noTourStep,
          noDataText: noDataText,
          onAddClick: this.onAddClick,
          onDeleteClick: this._onDeleteClick,
          onEditClick: this._onEditClick,
          onItemMove: this.props.onItemMove,
          onNoDataClick: this._noDataMode === "add" ? this.onAddClick : undefined,
          onRowClick: this._onRowClick,
          onCheckboxClick: this._onCheckboxClick,
          sortedBy: this.props.sortedBy,
          sortFn: this.props.sortFn,
          selectedItems: this.state.selectedItems
        })}

        {this._renderActionBar(false)}

        {showPager && (
          <PreviousNextPager
            pageNumber={pageNumber}
            pageSize={pageSize}
            pageSizes={pageSizes}
            onPageSizeChange={this._onPageSizeChange}
            onNextClick={hasMore ? this._onNextPage : undefined}
            onPreviousClick={this.state.pageNumber > 1 ? this._onPreviousPage : undefined}
          />
        )}
      </div>
    );

    return (
      <div className={classNames(className, "grid_view")} style={this.props.style}>
        {this._renderActionBar(true)}

        {header}
        {this.props.pagerType === "scroll" ? (
          <InfiniteScroll
            loadMore={this._onNextPage}
            hasMore={this.state.hasMore}
            isStartPosition={!!this.state.data.length && this.state.data.length <= this.state.pageSize}
            loader={<div style={{ display: "flex", justifyContent: "center" }}>{i18n.loading}...</div>}
            isLoading={this.props.isLoading}
          >
            {content}
          </InfiniteScroll>
        ) : (
          content
        )}

        {this._renderAddModal()}
        {this._renderDeleteModal()}
        {this._renderEditModal()}
      </div>
    );
  }

  private _onModalClose = () => this.setState({ visibleModal: null });

  private _onFieldChange = (name: string, value: any) => {
    if (this.state.visibleModal === "Delete") {
      this.setState((s) => ({
        modalData: {
          ...s.modalData,
          [name]: value
        }
      }));
    } else {
      this.setState((s) => ({
        editingItem: {
          ...s.editingItem,
          [name]: value
        }
      }));
    }
  };

  private _onModalAction = async (mixed?: D | React.MouseEvent, modalData?: GenericMap<any>): Promise<any> => {
    const { visibleModal } = this.state;

    this.setState({ isModalActionInProgress: true });
    try {
      switch (visibleModal) {
        case "Add": {
          const add = this.props.add as GridViewAddPropsBase<T, D, ModalProps>;
          const item =
            mixed && typeof mixed === "object" && !("eventPhase" in mixed) ? (mixed as D) : this.state.editingItem!;
          await add.action(item, modalData ?? this.state.modalData);
          break;
        }
        case "Edit": {
          const edit = this.props.edit as GridViewEditPropsBase<T, D, ModalProps>;
          const item =
            mixed && typeof mixed === "object" && !("eventPhase" in mixed) ? (mixed as D) : this.state.editingItem!;
          await edit.action(item, this.state.originalItem!);
          break;
        }
        case "Delete": {
          const remove = this.props.remove as GridViewRemovePropsBase<T>;
          await remove.action(this.state.deletingItem!, modalData ?? this.state.modalData);
          break;
        }
      }

      this.setState({ visibleModal: null });
    } finally {
      this.setState({ isModalActionInProgress: false });
    }
  };

  public onAddClick = async () => {
    if (!this.props.add) throw new Error("BUG");

    if (!("Modal" in this.props.add)) {
      if (!this.props.add.onClick) throw new Error("BUG");

      const res = this.props.add.onClick();
      if (res instanceof Promise) {
        this.setState({ isAdding: true });
        try {
          await res;
        } finally {
          this.setState({ isAdding: false });
        }
      }
    } else {
      const { getEmptyDetail } = this.props.add;
      if (!getEmptyDetail) throw new Error("BUG");

      const ed = getEmptyDetail();
      const editingItem = ed instanceof Promise ? await ed : ed;
      this.setState({
        editingItem,
        originalItem: null,
        isModalLoading: false,
        modalData: {},
        visibleModal: "Add"
      });
    }
  };

  private _onDeleteClick = async (item: T) => {
    if (isFunction(this.props.remove)) {
      this.props.remove(item);
    } else {
      if (this.props.remove?.shouldShow && !this.props.remove?.shouldShow(item)) {
        await this.props.remove.action(item, {});
      } else {
        this.setState({
          deletingItem: item,
          isModalLoading: false,
          modalData: {},
          visibleModal: "Delete"
        });
      }
    }
  };

  private _onEditClick = async (item: T) => {
    if (!this.props.edit) return;

    if (isFunction(this.props.edit)) {
      this.props.edit(item);
    } else {
      const { getDetail, getEmptyDetail, isCustomAction, isVisible } = this.props.edit!;

      if (isCustomAction && isCustomAction(item)) return;

      const ed = getEmptyDetail();
      let editingItem = ed instanceof Promise ? await ed : ed;
      this.setState({
        editingItem,
        originalItem: null,
        modalData: {},
        visibleModal: "Edit",
        isModalLoading: true,
        isModalReadOnly: !isUndefined(isVisible) && !isVisible(item)
      });

      editingItem = await getDetail(item);

      this.setState({
        editingItem,
        originalItem: cloneDeep(editingItem),
        isModalLoading: false
      });
    }
  };

  private _onSearch = async (searchText: string) => {
    if (!this.props.filterFn) throw new Error("BUG");

    this.setState({
      pageNumber: 1,
      searchText
    });

    const pp = this.props.filterFn(searchText, this.state.filterValues);
    const data = await (pp instanceof Promise ? pp : Promise.resolve(pp));

    if (Array.isArray(data)) {
      this.setState({
        data: searchText ? data : this._getPagedData(data, 1, this.state.pageSize)
      });
    }
  };

  private _renderToolbarButton = (tb: GridViewToolbarButton<T>, index: number) => {
    if (!tb) return null;

    if (React.isValidElement(tb)) {
      return React.cloneElement(tb, { ...(tb.props ?? {}), key: index });
    }

    if (isFunction(tb)) {
      return tb({
        onAddClick: this.onAddClick,
        onDeleteClick: this._onDeleteClick,
        onEditClick: this._onEditClick,
        selectedItems: this.state.selectedItems
      });
    }

    if (tb === "add") {
      return (
        <Button
          key="add"
          name="add"
          isDisabled={this.props.disableButtons}
          onClick={this.onAddClick}
          icon={IconType.Add}
        >
          {this.props.add?.label || this.props.i18n.add}
        </Button>
      );
    }

    const lbd = tb as GridViewToolbarButtonDef;
    if (lbd.downloadUrl) {
      return (
        <DownloadLink
          key={lbd.label}
          url={lbd.downloadUrl}
          className="btn btn-link"
          icon={lbd.icon}
          label={lbd.label}
        />
      );
    }

    return (
      <Button
        key={lbd.label}
        type={lbd.type}
        onClick={lbd.onClick}
        icon={lbd.icon}
        isDisabled={!!lbd.disabledTooltip}
        tooltip={lbd.disabledTooltip}
      >
        {lbd.label}
      </Button>
    );
  };

  private _onChangeFilter = async (name: string, value: FilterValue) => {
    if (!this.props.filterFn) throw new Error("BUG");

    const resetFilters = (this.props.filters ?? []).find((x) => x.name === name)?.resetFilters ?? [];
    let filterValues = (this.props.filters ?? []).reduce(
      (res, x) => ({
        ...res,
        [x.name]: x.name === name ? value : resetFilters.some((y) => y === x.name) ? null : x.value
      }),
      {}
    );

    const pp = this.props.filterFn(this.state.searchText, filterValues);
    this.setState({ filterValues });
    const data = await (pp instanceof Promise ? pp : Promise.resolve(pp));

    if (Array.isArray(data)) {
      this.setState({
        data: data,
        pageNumber: 1
      });
    }
  };

  private _renderFilter = (filter: Filter<T>) => {
    const value = this.state.filterValues[filter.name];
    if (!filter.options.length) return null;

    return (
      <Select
        key={filter.name}
        name={filter.qa || filter.name}
        isRequired
        onChange={(value) => this._onChangeFilter(filter.name, value)}
        options={filter.options}
        style={this.props.fieldStyle}
        value={value}
        selectedLabel={
          <span>{filter.title ? filter.title : filter.options.find((x) => x.value === value)?.label || "-"}</span>
        }
      />
    );
  };

  private _renderFilterCloseBadge = (filter: Filter<T>) =>
    this.state.filterValues[filter.name] !== filter.defaultValue ? (
      <Badge
        key={filter.name}
        color={SystemColor.White}
        onClose={() => this._onChangeFilter(filter.name, filter.defaultValue)}
      >
        {filter.options.find((x) => x.value === this.state.filterValues[filter.name])?.label}
      </Badge>
    ) : null;

  private _renderActionBar = (isTop: boolean) => {
    const { add, footer, toolbar, useSearchUrl } = this.props;

    const bar = isTop ? toolbar : footer;
    const filters = this.props.filters ?? [];
    if (
      !bar ||
      (!bar.leftButtons?.length && !bar.showSearch && !bar.showAdd && !bar.rightButtons?.length && !filters.length)
    )
      return null;

    return (
      <div
        className={classNames({
          top_bar: isTop,
          "bottom_bar clearfix": !isTop
        })}
      >
        {!!bar.leftButtons && (
          <ActionBarButtons className="left_buttons" style={{ float: "left", display: "flex" }}>
            {bar.leftButtons.map(this._renderToolbarButton)}
            {filters.map(this._renderFilterCloseBadge)}
          </ActionBarButtons>
        )}
        <ActionBarButtons className="right_buttons" style={{ float: "right", display: "flex" }}>
          {!!this.state.data.length || this._isFiltered || !!bar.isAlwaysVisible
            ? bar.showSearch && (
                <ExpandableSearch
                  debounceMs={500}
                  onSearch={this._onSearch}
                  shouldSyncToQs={useSearchUrl}
                  style={this.props.fieldStyle}
                  text={this.state.searchText}
                />
              )
            : null}

          {!!this.state.data.length ||
          this._isFiltered ||
          !!bar.isAlwaysVisible ||
          this.props.isFiltersAlwaysVisible ? (
            this.props.FiltersComponent ? (
              <FilterDropdownComponent
                align={ContentAlign.Stretch}
                filters={filters}
                maxHeight={300}
                maxWidth={300}
                onChange={this._onChangeFilter}
                style={this.props.fieldStyle}
                values={this.state.filterValues}
                Content={this.props.FiltersComponent}
              />
            ) : (
              filters.filter((x) => !x.hideButton).map(this._renderFilter)
            )
          ) : null}

          {(bar.rightButtons || []).map(this._renderToolbarButton)}
          {bar.showAdd && !!add && (this.state.data.length || this._isFiltered || this._noDataMode !== "add") && (
            <Button
              isDisabled={
                this.props.disableButtons || this.state.isAdding || ("isDisabled" in add && add.isDisabled === true)
              }
              onClick={this.onAddClick}
              icon={IconType.Add}
              type={add.type}
              name="add"
              tourStep={add.tourStep}
            >
              {add.label || this.props.i18n.add}
            </Button>
          )}
        </ActionBarButtons>
      </div>
    );
  };

  private _renderAddModal() {
    if (!this.props.add || isFunction(this.props.add) || "onClick" in this.props.add) return null;

    const { getEmptyDetail, Modal } = this.props.add as GridViewAddPropsBase<T, D, ModalProps>;
    if (!Modal || !getEmptyDetail) throw new Error("Bug. Add modal is not defined");

    if (this.state.visibleModal !== "Add") return null;

    const { isModalActionInProgress, isModalLoading } = this.state;

    const add = this.props.add;
    return (
      <Modal
        onChange={this._onFieldChange}
        onClose={this._onModalClose}
        detailItem={this.state.editingItem!}
        originalDetailItem={this.state.originalItem!}
        isAdd={true}
        isLoading={isModalLoading}
        isReadonly={false}
        isSubmitting={isModalActionInProgress}
        isVisible={this.state.visibleModal === "Add"}
        submit={this._onModalAction}
        {...(add.modalProps as ModalProps)}
      />
    );
  }

  private _renderDeleteModal() {
    if (!this.props.remove || isFunction(this.props.remove)) return null;
    if (this.state.visibleModal !== "Delete") return null;

    const { deletingItem, isModalLoading, isModalActionInProgress } = this.state;
    const { onClose, onOpen } = this.props.remove;

    const Modal = this.props.remove.Modal;
    const isVisible = this.state.visibleModal === "Delete";
    if (React.isValidElement(Modal) || Modal!["wrappedComponent"] || isFunction(Modal)) {
      const DeleteModalComponent = Modal as React.ComponentType<GridViewRemoveModalProps<T>>;
      if (isVisible && onOpen) onOpen(deletingItem!);

      return (
        <DeleteModalComponent
          onChange={this._onFieldChange}
          onClose={() => {
            this._onModalClose();
            onClose && onClose();
          }}
          isLoading={isModalLoading}
          item={deletingItem!}
          isDeleting={isModalActionInProgress}
          isVisible={this.state.visibleModal === "Delete"}
          delete={this._onModalAction}
          data={this.state.modalData as any}
        />
      );
    }

    const { buttonCaption, confirmText, message, title, toast } = Modal as GridViewRemoveModalInfo<T>;
    return (
      <DeleteModal
        buttonCaption={buttonCaption}
        confirmText={confirmText}
        onClose={this._onModalClose}
        isVisible={this.state.visibleModal === "Delete"}
        title={isFunction(title) ? title(deletingItem!) : title}
        message={isFunction(message) ? message(deletingItem!) : message}
        onDelete={(data) => this._onModalAction(undefined, data)}
        isLoading={isModalLoading}
        toast={isNull(toast) ? undefined : toast ?? this.props.i18n.deleted}
      />
    );
  }

  private _renderEditModal() {
    if (!this.props.edit || isFunction(this.props.edit) || "onClick" in this.props.edit) return null;

    if (this.state.visibleModal !== "Edit") return null;

    const { Modal } = this.props.edit!;

    const { isModalActionInProgress, isModalLoading, editingItem, isModalReadOnly, originalItem } = this.state;

    const edit = this.props.edit;
    return (
      <Modal
        onChange={this._onFieldChange}
        onClose={this._onModalClose}
        isAdd={false}
        isLoading={isModalLoading}
        isReadonly={isModalReadOnly}
        detailItem={editingItem!}
        originalDetailItem={originalItem!}
        isSubmitting={isModalActionInProgress}
        isVisible={this.state.visibleModal === "Edit"}
        submit={this._onModalAction}
        {...(edit.modalProps as ModalProps)}
      />
    );
  }

  private _getIsItemClickable = (item: T | GridGroupInfo) => {
    if (isGroupItem(item)) return !!this.props.onGroupClick;

    if (this.props.isRowClickable) return this.props.isRowClickable(item);

    if (this.props.onRowClick) return true;

    return !this.props.disableButtons && !!this.props.edit;
  };

  private _onRowClick = (item: T | GridGroupInfo) => {
    if (isGroupItem(item)) {
      this.props.onGroupClick && this.props.onGroupClick(item as unknown as GridGroupInfo<T>);
      return;
    }

    if (this.props.isRowClickable && !this.props.isRowClickable(item)) return;

    if (this.props.onRowClick) {
      this.props.onRowClick(item);
    } else {
      !this.props.disableButtons && this._onEditClick(item);
    }
  };

  private _onCheckboxClick = (isSelected: boolean, data: T[]) => {
    if (isSelected) {
      this.setState({ selectedItems: [...(this.state.selectedItems ?? []), ...data] });
    } else {
      this.setState({
        selectedItems: this.state.selectedItems?.filter(
          (x) => !data.some((y) => y[this.props.idKey] === x[this.props.idKey])
        )
      });
    }
  };
}
